import { batch, computed, signal, type ReadonlySignal, type Signal } from "@preact/signals";
import { downloadZip } from "client-zip";
import {
  ACCEPTED_MIME,
  LIMITS,
  REPLICA_HEADER,
  type BlobResponse,
  type Card,
  type CardFields,
  type CardKind,
  type Device,
  type Doc,
  type Link,
  type LiveMessage,
  type Op,
  type OpResult,
  type OpStatus,
  type Pin,
  type Prefs,
  type PullResponse,
  type PushResponse,
  type Space,
  type SpaceCard,
  type SyncEntity,
  type Tag,
} from "../../../shared/types.ts";
import { kindOfMime, SNIFF_BYTES, sniffMime } from "../../../shared/mime.ts";
import { normalizeTag, normalizeUrl } from "../../../shared/normalize.ts";
import { isQuoteText } from "../../../shared/text.ts";
import { clearLocalData, openLocal, openShareInbox, type LocalDB, type OutboxEntry } from "./db.ts";
import { decodeImage, palette, sha256Hex } from "./image.ts";
import { csv, markdownDoc, textDoc, unzip } from "./transfer.ts";
import type { BatchAction, CardStore, ImportReport, Notice, PrefValues, SyncStatus } from "./types.ts";

// Réplica local (IndexedDB) + fila de saída + sync por checkpoint. Spec §6.4 e §7.3. Guia da API: API.md.

/** Rede e relógio injetáveis (os testes rodam em node com um servidor falso). */
export interface StoreDeps {
  fetch: (url: string, init?: RequestInit) => Promise<Response>;
  webSocket: (url: string) => WebSocket;
  clock: { now(): number; setTimeout(fn: () => void, ms: number): unknown; clearTimeout(handle: unknown): void };
  /** Recebe focus, online e offline (window no navegador). */
  events: EventTarget | null;
  origin: string;
  decodeImage: typeof decodeImage;
  /** false = certeza de estar sem rede (navigator.onLine): o ciclo nem tenta, e as ops seguem abertas a novas edições. */
  online(): boolean;
  /** Pede ao navegador para não apagar a réplica sob pressão de espaço (§7.10). Chamado a cada start; o navegador decide. */
  persist(): Promise<unknown>;
  /** XHR para o PUT do arquivo (o fetch não mede o envio). null (node, testes): fetch, sem progresso. */
  xhr: (() => XMLHttpRequest) | null;
}

export type LocalStore = CardStore & {
  /** cardId → object URL da imagem ainda sem blob no servidor (não persiste; a UI usa enquanto não há primaryBlobId). */
  readonly previews: ReadonlySignal<Map<string, string>>;
  /** cardId → % do arquivo já enviado, enquanto o PUT está no ar. */
  readonly progress: ReadonlySignal<Map<string, number>>;
  /** Push + pull agora; se já houver um ciclo, junta-se a ele e roda mais um no fim. */
  sync(): Promise<void>;
  /** Checkpoint do último pull gravado (diagnóstico em "Sobre este aparelho"). */
  checkpoint(): number;
};

type OutboxStore = { put(e: OutboxEntry): Promise<unknown>; delete(opId: string): Promise<unknown> };
type CreateOp = Extract<Op, { type: "card.create" }>;
type NoId<T> = T extends unknown ? Omit<T, "opId"> : never;

/** Mudança de uma entidade local (null = some). A chave de spaceCards é "spaceId:cardId", como no sync. */
type Row<T> = [string, T | null];
interface Changes {
  cards?: Row<Card>[];
  tags?: Row<Tag>[];
  spaces?: Row<Space>[];
  spaceCards?: Row<SpaceCard>[];
  pins?: Row<Pin>[];
  links?: Row<Link>[];
  prefs?: Prefs;
}
const MAPS = ["tags", "spaces", "spaceCards", "pins", "links"] as const;
const STORES = ["cards", ...MAPS] as const;
type StoreName = (typeof STORES)[number];
const STORE_OF: Record<SyncEntity, StoreName | "prefs"> = { card: "cards", tag: "tags", space: "spaces", spaceCard: "spaceCards", pin: "pins", link: "links", prefs: "prefs" };
const idbKey = (name: string, id: string): IDBValidKey => (name === "spaceCards" ? id.split(":") : id);
const scKey = (spaceId: string, cardId: string) => `${spaceId}:${cardId}`;
type AnyStore = { put(v: unknown): Promise<unknown>; delete(k: IDBValidKey): Promise<unknown>; get(k: IDBValidKey): Promise<{ rev?: number } | undefined> };
const os = (tx: unknown, name: string) => (tx as { objectStore(n: string): AnyStore }).objectStore(name);

/** Escritas de `ch` numa transação que já cobre os stores. */
function write(tx: unknown, ch: Changes): Promise<unknown>[] {
  const reqs: Promise<unknown>[] = [];
  for (const name of STORES) for (const [id, v] of ch[name] ?? []) reqs.push(v ? os(tx, name).put(v) : os(tx, name).delete(idbKey(name, id)));
  if (ch.prefs) reqs.push(os(tx, "meta").put({ key: "prefs", value: ch.prefs }));
  return reqs;
}

const B32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
let lastT = -1;
const lastR: number[] = new Array(16).fill(0);

/** ULID monotônico: no mesmo ms (ou com relógio voltando) incrementa a parte aleatória, então chave da outbox = ordem de criação. */
export function ulid(now: number): string {
  if (now > lastT) {
    lastT = now;
    crypto.getRandomValues(new Uint8Array(16)).forEach((b, i) => (lastR[i] = b % 32));
  } else for (let i = 15; i >= 0 && ++lastR[i] === 32; i--) lastR[i] = 0;
  let time = "";
  for (let t = lastT, i = 0; i < 10; i++, t = Math.floor(t / 32)) time = B32[t % 32] + time;
  return time + lastR.map((r) => B32[r]).join("");
}

export function blankCard(id: string, kind: CardKind, now: number): Card {
  return {
    id, kind, kindOrigin: "user", entityType: null, entity: null, title: null, titleOrigin: null,
    sourceUrl: null, canonicalUrl: null, sourceDomain: null, content: null, contentText: null, note: null, noteText: null,
    summary: null, ocrText: null, palette: null, dominantColor: null, primaryBlobId: null, coverBlobId: null,
    width: null, height: null, ingestState: "ready", ingestError: null, tagIds: [],
    isPrivate: false, action: null, completedAt: null, objects: [], language: null, keyPhrases: [],
    readerAvailable: false, spaceIds: [], lastReviewedAt: null,
    createdAt: now, modifiedAt: now, bumpedAt: now, deletedAt: null, rev: 0,
  };
}

const PREF_DEFAULTS: PrefValues = { theme: "system", density: "amplo", noteFont: "sans", captions: true, minimalist: false, serendipityCount: 12 };
const oneOf = (...xs: string[]) => (v: unknown) => xs.includes(v as string);
const isBool = (v: unknown) => typeof v === "boolean";
/** Valores aceitos, dentro do que o servidor valida (PREFS em src/domain/ops.ts): um valor fora recusaria a op inteira. */
const PREF_OK: Record<keyof PrefValues, (v: unknown) => boolean> = {
  theme: oneOf("system", "light", "dark"), density: oneOf("amplo", "medio", "compacto"), noteFont: oneOf("sans", "serif", "mono"),
  captions: isBool, minimalist: isBool,
  serendipityCount: (v) => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 100,
};
const prefOk = ([k, v]: [string, unknown]) => Object.hasOwn(PREF_OK, k) && PREF_OK[k as keyof PrefValues](v);

/** Etiqueta reservada (normalizada): card com ela é privado (§4.2). */
const PRIVATE = "privado";
const HEX = /^#[0-9a-fA-F]{6}$/;
const PIN_FULL = `Em foco cabem ${LIMITS.pins} cards. Tire um para fixar outro.`;
const NOTIFIED = "notified"; // Error.cause: o aviso já saiu
const notified = (code: string) => new Error(code, { cause: NOTIFIED });

/** Texto rico com conflito por baseRev (§7.3): "c" = content, "n" = note, "" = só metadados. */
const textOf = (f: object) => ("content" in f || "contentText" in f ? "c" : "") + ("note" in f || "noteText" in f ? "n" : "");

/** 'duplicate' repete o resultado gravado da primeira vez (rev, dedupedInto, conflictCopyId, error): este é o desfecho. */
const outcome = (r: OpResult): OpStatus =>
  r.status !== "duplicate" ? r.status : r.error ? "rejected" : r.conflictCopyId ? "conflict" : "applied";

const nextPos = (xs: Iterable<{ position: number | null }>) => Math.max(0, ...[...xs].map((x) => x.position ?? 0)) + 1;
const byPos = (a: { position: number | null }, b: { position: number | null }) => (a.position ?? Infinity) - (b.position ?? Infinity);
const live = (c: Card | undefined): c is Card => !!c && !c.deletedAt;
/** Extensão no zip exportado para cada formato aceito (o resto: a parte depois da barra, ex. image/png → .png). */
const EXT: Record<string, string> = { "image/jpeg": "jpg", "image/svg+xml": "svg", "video/quicktime": "mov", "audio/mpeg": "mp3", "audio/mp4": "m4a" };
const extOf = (type: string) => {
  const t = type.split(";")[0].trim().toLowerCase();
  return (ACCEPTED_MIME as readonly string[]).includes(t) ? `.${EXT[t] ?? t.split("/")[1]}` : "";
};

/** Palavras a partir das quais o texto sem aspas junto do link conta como trecho selecionado. */
const EXCERPT_WORDS = 25;

/**
 * Texto compartilhado junto com o link, sem o próprio link. null = só o link ou o título da página: card de link.
 * `selection`: trecho da página (o Chrome manda entre aspas e com #:~:text= no link; ou texto longo) → destaque com a
 * origem. Senão é comentário de quem compartilha ("olha isso", manchete) → anotação do card de link, que a ingestão lê.
 * Ex.: '"Trecho bom" https://a.com/x#:~:text=Trecho' → { text: "Trecho bom", selection: true }.
 */
export function sharedExcerpt(text: string | undefined, url: string, title?: string): { text: string; selection: boolean } | null {
  const rest = (text ?? "")
    .split(url)
    .join(" ")
    .replace(/[ \t]+/g, " ")
    .replace(/ ([.,;:!?)])/g, "$1")
    .replace(/^[\s:–—-]+|[\s:–—-]+$/g, "");
  // O Chrome põe o trecho selecionado entre aspas retas: não é citação de ninguém.
  const plain = rest.replace(/^"([^"]+)"$/, "$1");
  const same = (a: string, b: string) => normalizeTag(a) === normalizeTag(b);
  if (!/[\p{L}\p{N}]/u.test(plain) || (title && same(plain, title))) return null;
  // ponytail: limiar fixo de palavras; trecho curto sem aspas nem #:~:text= vira link com anotação (o texto fica).
  const selection = /^["“«„]/.test(rest) || url.includes("#:~:text=") || plain.split(/\s+/).length >= EXCERPT_WORDS;
  return { text: plain, selection };
}

/** Anotação nova somada à que o card já tinha (link que já estava salvo): nada se perde. */
export function appendNote(note: Doc | null, noteText: string | null, text: string): { note: Doc; noteText: string } {
  const add = textDoc(text);
  if (!note?.content?.length) return { note: add, noteText: text };
  return { note: { type: "doc", content: [...note.content, ...(add.content ?? [])] }, noteText: noteText ? `${noteText}\n\n${text}` : text };
}

/**
 * Célula do CSV para planilha: texto que começa com = + - @ (ou tab/CR) ganha um ' na frente para não virar fórmula
 * (título de página vem de fora); acima do teto de célula do Excel, cortado. O .jsonl leva o texto inteiro.
 */
const cell = (v: string | null) => {
  const s = v ?? "";
  const t = s.length > 32_000 ? `${s.slice(0, 32_000)}…` : s;
  return /^[=+\-@\t\r]/.test(t) ? `'${t}` : t;
};

/** Primeira URL http(s) de um texto livre, sem a pontuação da frase; um ")" que fecha um "(" da própria URL fica. */
function urlIn(s: string | undefined): string | undefined {
  let u = s?.match(/https?:\/\/[^\s<>"]+/)?.[0];
  const n = (c: string) => u!.split(c).length - 1;
  for (; u && /[).,;:!?]$/.test(u); u = u.slice(0, -1)) if (u.endsWith(")") && n("(") >= n(")")) break;
  return u;
}

function defaults(): StoreDeps {
  return {
    fetch: (url, init) => fetch(url, init),
    webSocket: (url) => new WebSocket(url),
    clock: { now: () => Date.now(), setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (h) => clearTimeout(h as number) },
    events: typeof window === "undefined" ? null : window,
    origin: typeof location === "undefined" ? "" : location.origin,
    decodeImage,
    online: () => typeof navigator === "undefined" || navigator.onLine !== false,
    persist: async () => {
      const s = typeof navigator === "undefined" ? undefined : navigator.storage;
      if (s?.persist && !(await s.persisted?.())) await s.persist();
    },
    xhr: typeof XMLHttpRequest === "function" ? () => new XMLHttpRequest() : null,
  };
}

/**
 * O IndexedDB guarda File/Blob? A Navegação Privada do Safari recusa, e a transação que tentou fica presa (o banco inteiro
 * deixaria de responder). Testado num banco à parte, que é apagado em seguida; sem resposta em 3 s, vale como não.
 */
function blobsStorable(): Promise<boolean> {
  if (typeof indexedDB === "undefined") return Promise.resolve(false);
  return new Promise((ok) => {
    const name = "faig-probe";
    const timer = setTimeout(() => ok(false), 3000);
    const done = (v: boolean, db?: IDBDatabase) => {
      clearTimeout(timer);
      db?.close();
      if (v) indexedDB.deleteDatabase(name);
      ok(v);
    };
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => req.result.createObjectStore("b");
    req.onerror = () => done(false);
    req.onsuccess = () => {
      const db = req.result;
      try {
        const tx = db.transaction("b", "readwrite");
        tx.objectStore("b").put(new Blob(["faig"]), "k");
        tx.oncomplete = () => done(true, db);
        tx.onerror = tx.onabort = () => done(false, db);
      } catch {
        done(false, db);
      }
    };
  });
}

/** Envio de arquivo sem nenhum byte andar por este tempo: conexão morta, aborta e tenta de novo. */
const STALL_MS = 30_000;
/** Op de criação cujo arquivo o servidor recusou (4xx): os bytes ficam aqui e a op sai do caminho das outras. */
const parked = (e: OutboxEntry) => !!e.blobSha && !!e.lastError?.startsWith("refused");
const cardOf = (e: OutboxEntry) => ("cardId" in e.op ? e.op.cardId : "");
/** Fila que pode subir agora: sem as ops de cards com arquivo recusado (a criação deles não vai, as edições esperam). */
function sendable(queue: OutboxEntry[]): OutboxEntry[] {
  const held = new Set(queue.filter(parked).map(cardOf));
  return held.size ? queue.filter((e) => !held.has(cardOf(e))) : queue;
}

export function createCardStore(overrides: Partial<StoreDeps> = {}): LocalStore {
  const deps = { ...defaults(), ...overrides };
  const { clock } = deps;
  const all = signal(new Map<string, Card>());
  const tags = signal(new Map<string, Tag>());
  const spaceMap = signal(new Map<string, Space>());
  const scMap = signal(new Map<string, SpaceCard>());
  const pinMap = signal(new Map<string, Pin>());
  const linkMap = signal(new Map<string, Link>());
  const prefsRow = signal<Prefs | null>(null);
  const maps = { tags, spaces: spaceMap, spaceCards: scMap, pins: pinMap, links: linkMap };
  const notices = signal<Notice[]>([]);
  const status = signal<SyncStatus>({ online: true, syncing: false, pending: 0, lastSyncAt: null, error: null });
  // Object URL da imagem enquanto o servidor não a serve: revogado quando o card ganha primaryBlobId ou some.
  const previews = signal(new Map<string, string>());
  const progress = signal(new Map<string, number>());
  const setProgress = (id: string, pct: number | null) => {
    if ((progress.peek().get(id) ?? null) === pct) return;
    const m = new Map(progress.peek());
    if (pct === null) m.delete(id);
    else m.set(id, pct);
    progress.value = m;
  };

  const cards = computed(() => [...all.value.values()].filter((c) => !c.deletedAt).sort((a, b) => b.bumpedAt - a.bumpedAt));
  const trash = computed(() => [...all.value.values()].filter((c) => c.deletedAt).sort((a, b) => b.deletedAt! - a.deletedAt!));
  const spaces = computed(() => [...spaceMap.value.values()].sort((a, b) => a.position - b.position || (a.id < b.id ? -1 : 1)));
  const spaceCards = computed(() => {
    const out = new Map<string, SpaceCard[]>();
    for (const r of scMap.value.values()) out.set(r.spaceId, [...(out.get(r.spaceId) ?? []), r]);
    for (const list of out.values()) list.sort(byPos);
    return out;
  });
  const pinned = computed(() => [...pinMap.value.values()].filter((p) => live(all.value.get(p.cardId))).sort(byPos));
  const linkIndex = computed(() => {
    const from = new Map<string, Link[]>();
    const to = new Map<string, Link[]>();
    for (const l of linkMap.value.values()) {
      from.set(l.sourceId, [...(from.get(l.sourceId) ?? []), l]);
      to.set(l.targetId, [...(to.get(l.targetId) ?? []), l]);
    }
    return { from, to };
  });
  const prefs = computed<PrefValues>(() => ({ ...PREF_DEFAULTS, ...Object.fromEntries(Object.entries(prefsRow.value?.values ?? {}).filter(prefOk)) }));

  let db: LocalDB | null = null;
  let account: string | null = null;
  let gen = 0; // sobe em stop(): ciclo da sessão anterior não toca no banco, checkpoint nem signals da seguinte
  let stopped = true;
  let checkpoint = 0;
  let running: Promise<void> | null = null;
  let again = false;
  let timer: unknown;
  let retryMs = 0;
  let ws: WebSocket | null = null;
  let wsTimer: unknown;
  let wsRetryMs = 0;
  const inflight = new Set<string>(); // opIds no lote em envio: não podem ser reescritas
  // Texto que uma op nossa gravou sem conflito: cardId → {c|n: OpResult.rev}. Base exata das edições seguintes.
  const own = new Map<string, Record<string, number>>();

  /** Banco da sessão g. Lança se ela acabou (stop, ou start de outra conta); o ciclo chama de novo depois de cada await. */
  const need = (g = gen) => {
    if (g !== gen || !db) throw new Error("not_started");
    return db;
  };
  const newId = () => ulid(clock.now());
  const mk = (o: NoId<Op>) => ({ opId: newId(), ...o }) as Op;
  const setStatus = (p: Partial<SyncStatus>) => (status.value = { ...status.value, ...p });
  const notify = (n: Notice) => (notices.value = [...notices.value, n]);
  const fail = (message: string) => notify({ id: newId(), kind: "error", message });
  const refreshPending = async (g = gen) => {
    const pending = sendable(await need(g).getAll("outbox")).length;
    if (g === gen) setStatus({ pending });
  };

  /** Aplica mudanças de card em ordem sobre o valor atual (null = some da réplica). */
  function applyCards(changes: Row<Card>[]) {
    const m = new Map(all.value);
    let urls: Map<string, string> | null = null;
    for (const [id, c] of changes) {
      if (c) m.set(id, c);
      else m.delete(id);
      const url = (urls ?? previews.peek()).get(id);
      if (url && (!c || c.primaryBlobId)) {
        URL.revokeObjectURL(url);
        (urls ??= new Map(previews.peek())).delete(id);
      }
    }
    batch(() => {
      all.value = m;
      if (urls) previews.value = urls;
    });
  }
  /** Mudanças locais nos signals, todas de uma vez. */
  function apply(ch: Changes) {
    batch(() => {
      if (ch.cards?.length) applyCards(ch.cards);
      for (const name of MAPS) {
        const list = ch[name] as Row<unknown>[] | undefined;
        if (!list?.length) continue;
        const s = maps[name] as Signal<Map<string, unknown>>;
        const m = new Map(s.value);
        for (const [id, v] of list) v ? m.set(id, v) : m.delete(id);
        s.value = m;
      }
      if (ch.prefs) prefsRow.value = ch.prefs;
    });
  }

  /**
   * Desfaz `ch` nos signals: cada linha volta ao valor de antes, se ainda for a que `ch` pôs (uma mudança mais nova
   * no meio fica).
   */
  function undoOf(ch: Changes): () => void {
    const before = new Map<StoreName, Map<string, unknown>>(STORES.map((n) => [n, n === "cards" ? all.peek() : maps[n].peek()]));
    const prefsBefore = prefsRow.peek();
    return () => {
      const back: Changes = {};
      for (const name of STORES) {
        const now: Map<string, unknown> = name === "cards" ? all.peek() : maps[name].peek();
        const rows = ((ch[name] ?? []) as Row<unknown>[]).filter(([id, v]) => (now.get(id) ?? null) === v).map(([id]): Row<unknown> => [id, before.get(name)!.get(id) ?? null]);
        if (rows.length) (back[name] as Row<unknown>[]) = rows;
      }
      apply(back);
      if (ch.prefs && prefsRow.peek() === ch.prefs) prefsRow.value = prefsBefore;
    };
  }

  /**
   * Escrita otimista: signals na hora; entidades, ops e arquivo numa transação; depois sync. Se a transação falha
   * (IndexedDB recusou: Navegação Privada do Safari com arquivo, aparelho sem espaço), nada ficou gravado e o que
   * apareceu na hora sai de novo: card fantasma "Enviando" que some ao recarregar nunca.
   */
  async function commit(ch: Changes, ops: Op[], blob?: { sha256: string; file: Blob }) {
    const undo = undoOf(ch);
    apply(ch);
    const reqs: Promise<unknown>[] = [];
    let abort = () => {};
    try {
      const tx = need().transaction([...STORES, "meta", "outbox", "pendingBlobs"] as const, "readwrite");
      abort = () => tx.abort();
      const now = clock.now();
      reqs.push(...write(tx, ch), tx.done);
      if (blob) reqs.push(tx.objectStore("pendingBlobs").put(blob));
      for (const op of ops) reqs.push(tx.objectStore("outbox").put({ opId: op.opId, op, createdAt: now, attempts: 0, lastError: null, blobSha: blob?.sha256 }));
      await Promise.all(reqs);
    } catch (err) {
      for (const r of reqs) r.catch(() => {});
      try {
        abort(); // put que lançou na hora (DataCloneError): o resto da transação não pode ser gravado sozinho
      } catch {}
      undo();
      throw err;
    }
    await refreshPending();
    void sync();
  }

  // ---------- rede ----------

  /**
   * 401: entrar de novo, ou "account_mismatch" quando o cookie é de outra conta (a UI abre a outra). 408 (proxy da
   * operadora cortou o pedido lento), 429 e 5xx: o ciclo espera e tenta de novo.
   */
  async function checked(res: Response): Promise<Response> {
    if (res.status === 401) throw new Error((await mismatch(res)) ? "account_mismatch" : "unauthorized");
    if (res.status >= 500 || res.status === 429 || res.status === 408) throw new Error("server");
    return res;
  }
  /** Pedido do app com a conta desta réplica: se o cookie for de outra conta, o servidor recusa (401 account_mismatch). */
  function call(path: string, init: RequestInit = {}) {
    const headers = new Headers(init.headers);
    if (account) headers.set(REPLICA_HEADER, account);
    return deps.fetch(deps.origin + path, { ...init, headers });
  }
  /** 401 com o cookie de outra conta (status.error "account_mismatch": a UI abre a outra), não sessão terminada. */
  const mismatch = async (res: Response) => ((await res.json().catch(() => null)) as { code?: unknown } | null)?.code === "account_mismatch";

  async function api(path: string, init?: RequestInit): Promise<Response> {
    let res: Response;
    try {
      res = await call(path, init);
    } catch {
      throw new Error("offline");
    }
    return await checked(res);
  }

  /**
   * PUT do arquivo com o progresso em `progress` (cardId). Falha de rede lança Error("offline"). Conexão morta que o
   * sistema ainda não derrubou (troca de Wi-Fi para 4G): STALL_MS sem nenhum byte andar aborta, e o ciclo tenta de novo.
   */
  async function putFile(cardId: string, sha: string, file: Blob): Promise<Response> {
    const url = `${deps.origin}/v1/blobs/${sha}`;
    const make = deps.xhr;
    let dog: unknown;
    try {
      if (!make) return await call(`/v1/blobs/${sha}`, { method: "PUT", body: file, headers: file.type ? { "content-type": file.type } : {} });
      return await new Promise<Response>((ok, no) => {
        const x = make();
        const pet = () => {
          clock.clearTimeout(dog);
          dog = clock.setTimeout(() => x.abort(), STALL_MS);
        };
        x.open("PUT", url);
        if (file.type) x.setRequestHeader("content-type", file.type);
        if (account) x.setRequestHeader(REPLICA_HEADER, account);
        x.upload.onprogress = (e) => {
          pet();
          if (e.lengthComputable && e.total) setProgress(cardId, Math.min(99, Math.floor((e.loaded / e.total) * 100)));
        };
        x.onload = () => ok(new Response(x.responseText, { status: x.status, headers: { "content-type": x.getResponseHeader("content-type") ?? "text/plain" } }));
        x.onerror = x.onabort = x.ontimeout = () => no(new TypeError("upload failed"));
        pet();
        x.send(file);
      });
    } catch {
      throw new Error("offline");
    } finally {
      clock.clearTimeout(dog);
      setProgress(cardId, null);
    }
  }

  /** REST direto (fora da fila): falha vira aviso (título do problem+json do servidor) e a promise rejeita com Error(code). */
  async function rest(path: string, init: RequestInit = {}): Promise<Response> {
    let res: Response;
    try {
      res = await call(path, { credentials: "include", ...init });
    } catch {
      fail("Sem conexão com o servidor. Tente de novo.");
      throw notified("offline");
    }
    if (res.ok) return res;
    if (res.status === 401 && (await mismatch(res.clone()))) {
      if (!stopped) setStatus({ error: "account_mismatch" });
      throw notified("account_mismatch");
    }
    const p = (await res.json().catch(() => null)) as { title?: unknown; code?: unknown } | null;
    fail(res.status === 401 ? "Sua sessão terminou. Entre de novo." : typeof p?.title === "string" ? p.title : "O servidor recusou o pedido. Tente de novo.");
    throw notified(typeof p?.code === "string" ? p.code : `http_${res.status}`);
  }
  const getJson = async <T>(path: string, init?: RequestInit) => (await (await rest(path, init)).json()) as T;
  const enc = encodeURIComponent;

  async function putBlob(file: Blob): Promise<{ blobId: string; sha256: string }> {
    const sha256 = await sha256Hex(file);
    const res = await rest(`/v1/blobs/${sha256}`, { method: "PUT", body: file, headers: file.type ? { "content-type": file.type } : {} });
    return { blobId: ((await res.json()) as BlobResponse).blobId, sha256 };
  }

  /**
   * PUT do arquivo pendente. Devolve o blobId, ou null: sem os bytes aqui (a op e o card somem) ou recusado pelo
   * servidor (4xx). Recusado, nada se perde: os bytes ficam, o card vira "Falhou" com "Tentar de novo" e a op fica
   * parada (parked) sem segurar o resto da fila.
   */
  async function upload(g: number, e: OutboxEntry): Promise<string | null> {
    const op = e.op as CreateOp;
    const pending = await need(g).get("pendingBlobs", e.blobSha!);
    if (!pending) {
      const tx = need(g).transaction(["outbox", "cards"], "readwrite");
      await Promise.all([tx.objectStore("outbox").delete(e.opId), tx.objectStore("cards").delete(op.cardId), tx.done]);
      need(g);
      applyCards([[op.cardId, null]]);
      fail("Não foi possível enviar o arquivo: ele não estava mais neste aparelho.");
      return null;
    }
    const res = await checked(await putFile(op.cardId, e.blobSha!, pending.file));
    need(g);
    if (!res.ok) {
      const card = all.peek().get(op.cardId);
      const down = card && { ...card, ingestState: "failed" as const, ingestError: "upload_refused" };
      const tx = need(g).transaction(["outbox", "cards"], "readwrite");
      await Promise.all([tx.objectStore("outbox").put({ ...e, lastError: `refused_${res.status}` }), ...(down ? [tx.objectStore("cards").put(down)] : []), tx.done]);
      need(g);
      if (down) applyCards([[op.cardId, down]]);
      fail("O servidor não aceitou um arquivo. Ele continua neste aparelho: toque em Tentar de novo no card.");
      return null;
    }
    const { blobId } = (await res.json()) as BlobResponse;
    op.data.blobId = blobId;
    // O arquivo chegou: o card sai de "enviando" já (o contador "Enviando X de N" anda a cada envio, não só no pull).
    const card = all.peek().get(op.cardId);
    const sent = card?.ingestState === "uploading" ? { ...card, ingestState: "queued" as const } : null;
    const tx = need(g).transaction(["outbox", "cards"], "readwrite");
    // O arquivo fica em pendingBlobs até o servidor confirmar a op.
    await Promise.all([tx.objectStore("outbox").put(e), ...(sent ? [tx.objectStore("cards").put(sent)] : []), tx.done]);
    need(g);
    if (sent) applyCards([[op.cardId, sent]]);
    return blobId;
  }

  /**
   * Edição feita sobre texto que uma op nossa já gravou: o baseRev sobe para o rev exato dessa op (OpResult.rev),
   * senão o servidor vê conflito do aparelho consigo mesmo (o rev local só anda no pull).
   */
  function rebase(op: Extract<Op, { type: "card.update" }>) {
    const t = textOf(op.fields);
    const o = own.get(op.cardId);
    if (t && o) op.baseRev = Math.max(op.baseRev, Math.min(...[...t].map((k) => o[k] ?? 0)));
  }

  /**
   * Criação deduplicada: o card local some e vale o que já existia. O que a pessoa escreveu antes da resposta (anotação
   * e título na criação, edições, etiquetas, coleções e Em foco na fila) vai para esse card. A edição sai com baseRev 0
   * exato: se ele já tem anotação, o servidor guarda esta como cópia em conflito em vez de sobrescrever.
   */
  function moveTo(out: OutboxStore, create: OutboxEntry, into: string, queued: OutboxEntry[]): Promise<unknown>[] {
    if (create.op.type !== "card.create") return [];
    const { note, noteText, title } = create.op.data;
    // só o que tem conteúdo: um null aqui apagaria título ou anotação que o outro card já tem
    const carried = Object.fromEntries(Object.entries({ note, noteText, title }).filter(([, v]) => v != null)) as Partial<CardFields>;
    const reqs: Promise<unknown>[] = [];
    let first: OutboxEntry | undefined;
    for (const q of queued) {
      const op = q.op;
      if (!("cardId" in op) || op.cardId !== create.op.cardId || q.opId === create.opId) continue;
      if (op.type === "card.update") {
        if (!first) op.fields = { ...carried, ...op.fields }; // a edição da fila é mais nova
        first ??= q;
        op.baseRev = 0;
        q.exact = true;
      } else if (op.type === "card.delete" || op.type === "card.restore" || op.type === "tag.detach") {
        reqs.push(out.delete(q.opId)); // apagar, restaurar ou tirar etiqueta do card que sumiu não vale para o outro
        continue;
      }
      op.cardId = into;
      reqs.push(out.put(q));
    }
    if (!first && Object.keys(carried).length) {
      const opId = newId();
      reqs.push(out.put({ opId, op: { opId, type: "card.update", cardId: into, baseRev: 0, fields: carried }, createdAt: clock.now(), attempts: 0, lastError: null, exact: true }));
    }
    return reqs;
  }

  /** Envia um lote e aplica os resultados; ops com resultado saem da fila. */
  async function send(g: number, sent: OutboxEntry[]) {
    for (const e of sent) if (e.op.type === "card.update" && !e.exact) rebase(e.op);
    // Daqui em diante a op pode estar aplicada sem resposta (conexão caiu, 5xx no meio do lote): o reenvio volta
    // 'duplicate' e ignora campos novos, então ela fecha para edições (update() não junta mais nada nela).
    const marked = need(g).transaction("outbox", "readwrite");
    await Promise.all([...sent.map((e) => marked.store.put({ ...e, attempts: e.attempts + 1 })), marked.done]);
    const res = await api("/v1/sync/push", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ops: sent.map((e) => e.op) }) });
    if (!res.ok) throw new Error(`http_${res.status}`);
    const { results } = (await res.json()) as PushResponse;
    const gone: Row<Card>[] = [];
    const tx = need(g).transaction(["outbox", "cards", "pendingBlobs"], "readwrite");
    const out = tx.objectStore("outbox");
    const queued = results.some((r) => r.dedupedInto) ? await out.getAll() : [];
    const reqs: Promise<unknown>[] = [];
    for (const r of results) {
      const e = sent.find((s) => s.opId === r.opId);
      if (!e) continue;
      const st = outcome(r);
      const op = e.op;
      const cardId = "cardId" in op ? op.cardId : "";
      reqs.push(out.delete(r.opId));
      if (e.blobSha) reqs.push(tx.objectStore("pendingBlobs").delete(e.blobSha));
      if (op.type === "card.create" && (r.dedupedInto || st === "rejected")) gone.push([cardId, null]), reqs.push(tx.objectStore("cards").delete(cardId));
      if (r.dedupedInto) reqs.push(...moveTo(out, e, r.dedupedInto, queued));
      // Texto que esta op gravou sem conflito (a criação grava texto e anotação): o rev dela é a base da próxima edição.
      const rev = st === "applied" && !r.dedupedInto && !e.exact ? r.rev : undefined;
      const t = rev === undefined ? "" : op.type === "card.create" ? "cn" : op.type === "card.update" ? textOf(op.fields) : "";
      for (const k of t) own.set(cardId, { ...own.get(cardId), [k]: rev! });
      if (r.dedupedInto) {
        // Criação sem card local: createLink achou o gêmeo na réplica e o aviso já saiu lá.
        if (all.peek().has(cardId)) notify({ id: newId(), kind: "deduped", cardId: r.dedupedInto });
      } else if (st === "conflict") notify({ id: newId(), kind: "conflict", cardId, copyId: r.conflictCopyId ?? "" });
      // ponytail: op recusada que não é create deixa o estado otimista até a entidade mudar no servidor de novo
      // (entidade nova, rev 0, sai em dropLeftovers).
      else if (st === "rejected") fail(r.error === "limit" ? PIN_FULL : "Uma alteração foi recusada pelo servidor e descartada.");
    }
    await Promise.all([...reqs, tx.done]);
    need(g);
    applyCards(gone);
    if (!reqs.length) throw new Error("server"); // nenhuma op reconhecida: não reenviar o mesmo lote em laço
  }

  /**
   * Esvazia a fila em lotes de ≤ 50, do mais antigo para o mais novo. No lote, o que não tem arquivo a subir vai antes
   * (a nota não espera a foto num envio lento), e os arquivos sobem um por vez, cada um com o seu push: o que entra na
   * fila durante um envio sai antes do arquivo seguinte.
   */
  async function push(g: number) {
    for (;;) {
      const entries = sendable(await need(g).getAll("outbox")).slice(0, LIMITS.pushOps);
      if (!entries.length) return;
      // Vão no lote seguinte: edição atrás de outra criação/edição do mesmo card (o baseRev depende dessa resposta) e
      // qualquer op atrás da criação do mesmo card (no dedupe ela muda para o card que já existia).
      const lot: OutboxEntry[] = [];
      const created = new Set<string>();
      const edited = new Set<string>();
      for (const e of entries) {
        const type = e.op.type;
        const cardId = "cardId" in e.op ? e.op.cardId : "";
        if (cardId && (type === "card.update" ? edited.has(cardId) : type !== "card.create" && created.has(cardId))) break;
        if (type === "card.create") created.add(cardId);
        if (type === "card.create" || type === "card.update") edited.add(cardId);
        lot.push(e);
      }
      const waits = (e: OutboxEntry) => e.op.type === "card.create" && !!e.blobSha && !e.op.data.blobId;
      const plain = lot.filter((e) => !waits(e));
      const file = lot.find(waits);
      for (const e of lot) inflight.add(e.opId);
      try {
        if (plain.length) await send(g, plain);
        if (file && (await upload(g, file))) await send(g, [file]);
      } finally {
        for (const e of lot) inflight.delete(e.opId);
      }
      await refreshPending(g);
    }
  }

  /** Pull até more=false; cada página (todas as entidades, lápides incluídas) numa transação junto com o checkpoint. */
  async function pull(g: number) {
    for (;;) {
      const res = await api(`/v1/sync?since=${checkpoint}&limit=${LIMITS.pullPage}`);
      if (!res.ok) throw new Error(`http_${res.status}`);
      const page = (await res.json()) as PullResponse;
      const ch: Changes = {};
      for (const it of page.items) {
        const name = STORE_OF[it.entity];
        if (name === "prefs") {
          if (!it.deleted) ch.prefs = it.data as Prefs;
        } else if (name) ((ch[name] ??= []) as Row<unknown>[]).push([it.id, it.deleted ? null : it.data]);
      }
      const tx = need(g).transaction([...STORES, "meta"] as const, "readwrite");
      await Promise.all([...write(tx, ch), tx.objectStore("meta").put({ key: "checkpoint", value: page.checkpoint }), tx.done]);
      need(g);
      checkpoint = page.checkpoint;
      // ponytail: estado do servidor sobrescreve edição local feita durante o pull; o próximo ciclo reenvia e corrige.
      apply(ch);
      if (!page.more) break;
    }
    await dropLeftovers(g);
  }

  /**
   * Sobras otimistas (rev 0) que o pull não trouxe. Card: sem op na fila (a criação já teve resposta, mas o card não
   * existe no servidor; rede de segurança: o 'duplicate' já repete dedupedInto). Etiqueta, coleção, pertença e Em foco
   * provisórios: só com a fila vazia (o servidor recusou a op, ou a versão dele já veio no pull). Só sai o que já está
   * no IndexedDB, então uma escrita otimista ainda sem transação fica.
   */
  async function dropLeftovers(g: number) {
    const zero = (m: Map<string, { rev: number }>) => [...m].filter(([, v]) => v.rev === 0).map(([k]) => k);
    const local: [StoreName, string[]][] = [["cards", zero(all.value)], ...MAPS.map((n): [StoreName, string[]] => [n, zero(maps[n].value)])];
    if (local.every(([, ids]) => !ids.length)) return;
    const tx = need(g).transaction([...STORES, "outbox"] as const, "readwrite");
    const queue = await tx.objectStore("outbox").getAll();
    const queued = new Set(queue.map((e) => ("cardId" in e.op ? e.op.cardId : "")));
    const ch: Changes = {};
    for (const [name, ids] of local) {
      if (name !== "cards" && queue.length) continue;
      for (const id of ids) {
        if (name === "cards" && queued.has(id)) continue;
        if ((await os(tx, name).get(idbKey(name, id)))?.rev === 0) ((ch[name] ??= []) as Row<unknown>[]).push([id, null]);
      }
    }
    await Promise.all([...write(tx, ch), tx.done]);
    need(g);
    apply(ch);
  }

  async function cycle(g: number): Promise<boolean> {
    setStatus({ syncing: true });
    try {
      if (!deps.online()) throw new Error("offline");
      await push(g);
      await pull(g);
      const lastSyncAt = clock.now();
      await need(g).put("meta", { key: "lastSyncAt", value: lastSyncAt });
      need(g);
      // A rede pode ter caído durante o ciclo (evento "offline" já tratado): o fim do ciclo não volta a dizer online.
      setStatus({ online: deps.online(), error: null, lastSyncAt });
      retryMs = 0;
      schedule(60_000); // reserva para quando o WebSocket falha calado
      return true;
    } catch (err) {
      if (g !== gen) return false; // sessão encerrada: sem status nem nova tentativa
      const code = err instanceof Error ? err.message : "unknown";
      if (code === "unauthorized" || code === "account_mismatch") {
        setStatus({ error: code }); // sem nova tentativa: precisa entrar de novo (ou a UI abre a outra conta)
        return false;
      }
      setStatus({ online: code !== "offline", error: code === "offline" ? null : code });
      retryMs = Math.min(60_000, retryMs ? retryMs * 2 : 1_000);
      schedule(retryMs);
      return false;
    } finally {
      if (g === gen) setStatus({ syncing: false });
    }
  }

  function sync(): Promise<void> {
    if (stopped) return Promise.resolve();
    if (running) {
      again = true;
      return running;
    }
    const g = gen;
    const run: Promise<void> = (async () => {
      let ok = true;
      do {
        again = false;
        ok = await cycle(g);
      } while (ok && again && g === gen);
    })().finally(() => {
      if (running === run) running = null;
    });
    return (running = run);
  }

  function schedule(ms: number) {
    clock.clearTimeout(timer);
    timer = clock.setTimeout(() => void sync(), ms);
  }

  function connect() {
    if (stopped) return;
    const sock = deps.webSocket(deps.origin.replace(/^http/, "ws") + "/v1/sync/live");
    ws = sock;
    sock.onopen = () => {
      // Também na primeira vez: o primeiro pull pode terminar antes do socket abrir, e um aviso nesse meio se perderia (o
      // card de outro aparelho só viria no próximo sync). Reconectou: pode ter perdido avisos. Um pull a mais, juntado.
      void sync();
      wsRetryMs = 0;
    };
    sock.onmessage = (ev) => {
      let cp = Infinity;
      try {
        cp = (JSON.parse(String(ev.data)) as LiveMessage).checkpoint;
      } catch {}
      if (cp > checkpoint) void sync();
    };
    sock.onclose = () => {
      if (ws !== sock || stopped) return;
      ws = null;
      wsRetryMs = Math.min(60_000, wsRetryMs ? wsRetryMs * 2 : 1_000);
      wsTimer = clock.setTimeout(connect, wsRetryMs);
    };
  }

  const onWake = () => void sync();
  const onOffline = () => setStatus({ online: false });

  // ---------- cards ----------

  /**
   * `keep`: anotação e título da pessoa que o card recriado (retry) leva junto. `replaces`: o card com falha vai para a
   * lixeira no mesmo commit (a grade nunca fica sem ele; a op de apagar sai antes da de criar).
   */
  async function createLink(url: string, tagNames?: string[], keep: Pick<Partial<CardFields>, "note" | "noteText" | "title"> = {}, replaces?: Card): Promise<string> {
    const domain = new URL(url).hostname; // lança se a URL é inválida
    const now = clock.now();
    const id = newId();
    // opId é a ordem de envio: a op de apagar nasce antes da de criar.
    const drop: Op[] = replaces ? [mk({ type: "card.delete", cardId: replaces.id })] : [];
    const create = mk({ type: "card.create", cardId: id, data: { kind: "link", createdAt: now, sourceUrl: url, tags: tagNames, ...keep } });
    // Mesma URL já na réplica (resposta do servidor ainda a caminho, ou offline): sobe o card que existe em vez de
    // desenhar um segundo. A criação vai mesmo assim; o servidor deduplica nele (bumpedAt, etiquetas) e devolve dedupedInto.
    const canonical = normalizeUrl(url)?.canonical;
    const twin = [...all.value.values()].find(
      (c) => !c.deletedAt && c.id !== replaces?.id && canonical === (c.canonicalUrl ?? (c.kind === "link" && c.sourceUrl ? normalizeUrl(c.sourceUrl)?.canonical : null)),
    );
    if (canonical && twin) {
      notify({ id: newId(), kind: "deduped", cardId: twin.id });
      await commit({ cards: [[twin.id, { ...twin, bumpedAt: now }]] }, [create]);
      return twin.id;
    }
    const card: Card = { ...blankCard(id, "link", now), ...keep, titleOrigin: keep.title ? "user" : null, sourceUrl: url, sourceDomain: domain, ingestState: "queued" };
    await commit({ cards: replaces ? [[replaces.id, { ...replaces, deletedAt: now }], [id, card]] : [[id, card]] }, [...drop, create]);
    return id;
  }

  async function note(doc: Doc, text: string, title?: string) {
    const now = clock.now();
    const id = newId();
    const card: Card = { ...blankCard(id, "note", now), content: doc, contentText: text, ...(title ? { title, titleOrigin: "user" as const } : {}) };
    await commit({ cards: [[id, card]] }, [mk({ type: "card.create", cardId: id, data: { kind: "note", createdAt: now, content: doc, contentText: text, ...(title ? { title } : {}) } })]);
    return id;
  }
  /** Trecho com a página de origem: citação se vier entre aspas, senão destaque (o texto fica, o link vira a origem). */
  async function excerpt(text: string, url: string, title?: string) {
    const now = clock.now();
    const id = newId();
    const kind: CardKind = isQuoteText(text) ? "quote" : "highlight";
    const doc = textDoc(text);
    const t = title?.trim().slice(0, 1000) || undefined;
    const card: Card = {
      ...blankCard(id, kind, now), content: doc, contentText: text, sourceUrl: url, sourceDomain: new URL(url).hostname,
      ...(t ? { title: t, titleOrigin: "user" as const } : {}),
    };
    await commit({ cards: [[id, card]] }, [mk({ type: "card.create", cardId: id, data: { kind, createdAt: now, content: doc, contentText: text, sourceUrl: url, ...(t ? { title: t } : {}) } })]);
    return id;
  }
  /** Link compartilhado com um comentário: ele vira a anotação (somada à do card, se o link já estava salvo). */
  async function commentedLink(url: string, comment?: string) {
    const id = await createLink(url, undefined, comment ? { note: textDoc(comment), noteText: comment } : {});
    const c = all.peek().get(id);
    if (comment && c && !c.noteText?.includes(comment)) await update(id, appendNote(c.note, c.noteText, comment));
    return id;
  }
  /** .md (Markdown convertido) ou texto puro → nota com o nome do arquivo como título (alvo de [[ ]]). */
  const noteOf = async (f: File) => {
    const text = await f.text();
    return { text, doc: /\.(md|markdown)$/i.test(f.name) ? markdownDoc(text) : textDoc(text), title: f.name.replace(/\.[^.]+$/, "") || undefined };
  };
  /** Arquivo de texto compartilhado: nota pela fila (vale offline). */
  const textFile = async (f: File) => {
    const n = await noteOf(f);
    return note(n.doc, n.text, n.title);
  };

  /**
   * Um arquivo por vez, na ordem em que chegaram: ler, calcular o hash e decodificar uma foto de 12 MP ocupa ~50 MB, e
   * vinte fotos escolhidas juntas derrubariam a aba no iPhone.
   */
  let filing: Promise<unknown> = Promise.resolve();
  let storable: Promise<boolean> | undefined;
  function createFile(file: File, tagNames?: string[]): Promise<string> {
    const run = filing.then(() => fileCard(file, tagNames));
    filing = run.catch(() => {});
    return run;
  }

  async function fileCard(file: File, tagNames?: string[]) {
    // Formato pelos bytes mágicos, o mesmo teste do servidor: o tipo que o sistema dá varia (audio/x-m4a, vazio...).
    const mime = sniffMime(new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer()));
    if (!mime) {
      fail("Esse formato de arquivo não é aceito.");
      throw notified("unsupported_type");
    }
    if (file.size > LIMITS.blobBytes) {
      fail("O arquivo passa de 100 MB.");
      throw notified("too_large");
    }
    const kind = kindOfMime(mime);
    const image = kind === "image";
    // ponytail: formato que o navegador não decodifica (HEIC fora do Safari, alguns SVG) sobe sem paleta nem dimensões.
    const [sha, decoded] = await Promise.all([sha256Hex(file), image ? deps.decodeImage(file).catch(() => null) : null]);
    const colors = decoded ? palette(decoded.rgba) : null;
    const fields = image ? { palette: colors?.palette ?? null, dominantColor: colors?.dominantColor ?? null, width: decoded?.width ?? null, height: decoded?.height ?? null } : {};
    // PDF, vídeo e áudio: o nome do arquivo (sem a extensão) é o título.
    const title = image ? undefined : file.name.replace(/\.[^.]+$/, "").trim().slice(0, 1000) || undefined;
    const now = clock.now();
    const id = newId();
    const card: Card = { ...blankCard(id, kind, now), ...fields, ...(title ? { title, titleOrigin: "user" as const } : {}), ingestState: "uploading" };
    const op = mk({ type: "card.create", cardId: id, data: { kind, createdAt: now, ...fields, ...(title ? { title } : {}), tags: tagNames } }) as CreateOp;
    const show = () => (previews.value = new Map(previews.value).set(id, URL.createObjectURL(file)));
    show();
    if (await (storable ??= blobsStorable())) {
      try {
        await commit({ cards: [[id, card]] }, [op], { sha256: sha, file });
        return id;
      } catch (err) {
        if ((err as Error).message === "not_started") throw err;
        show(); // recusado mesmo assim (aparelho sem espaço): o commit desfez o card e a prévia, que voltam aqui
      }
    }
    // Sem onde guardar os bytes (Navegação Privada do Safari, aparelho sem espaço): com rede eles sobem agora e só a op
    // entra na fila; sem rede, a pessoa fica sabendo (nada de card "Enviando" que some ao recarregar).
    applyCards([[id, card]]);
    try {
      if (!deps.online()) {
        fail("Este navegador não deixou guardar o arquivo no aparelho (navegação privada ou sem espaço). Tente com conexão ou fora da navegação privada.");
        throw notified("no_room");
      }
      const res = await putFile(id, sha, file).catch(() => {
        fail("Sem conexão com o servidor. Tente de novo.");
        throw notified("offline");
      });
      if (!res.ok) {
        fail(res.status === 401 ? "Sua sessão terminou. Entre de novo." : "O servidor não aceitou o arquivo. Tente de novo.");
        throw notified(`http_${res.status}`);
      }
      op.data.blobId = ((await res.json()) as BlobResponse).blobId;
      await commit({ cards: [[id, { ...card, ingestState: "queued" }]] }, [op]);
      return id;
    } catch (err) {
      applyCards([[id, null]]);
      throw err;
    }
  }

  async function update(id: string, fields: Partial<CardFields>) {
    const card = all.value.get(id);
    if (!card) return;
    // Troca de tipo sem origem dita é escolha da pessoa. A origem vai explícita na op: uma edição juntada a ela depois
    // (Object.assign abaixo) nunca herda a origem de outra troca.
    if (("kind" in fields || "entityType" in fields) && !fields.kindOrigin) fields = { ...fields, kindOrigin: "user" };
    const next: Card = { ...card, ...fields, modifiedAt: clock.now() };
    if ("title" in fields) next.titleOrigin = "user"; // como o servidor grava
    applyCards([[id, next]]);
    // Edição em sequência (autosave, anotação feita offline): entra na última criação/edição do card que nunca foi
    // enviada. Uma op já enviada fica fechada; a nova sai depois dela com baseRev = OpResult.rev (rebase).
    const t = textOf(fields);
    const tx = need().transaction(["cards", "outbox"], "readwrite");
    const last = (await tx.objectStore("outbox").getAll()).findLast((e) => (e.op.type === "card.create" || e.op.type === "card.update") && e.op.cardId === id);
    const into =
      !last || last.attempts || last.exact || inflight.has(last.opId) ? null
      : last.op.type === "card.create" ? last.op.data
      : last.op.type === "card.update" && (!t || textOf(last.op.fields) === t) ? last.op.fields
      : null;
    if (into) {
      Object.assign(into, fields);
      await Promise.all([tx.objectStore("cards").put(next), tx.objectStore("outbox").put(last!), tx.done]);
      return;
    }
    await tx.done;
    await commit({ cards: [[id, next]] }, [mk({ type: "card.update", cardId: id, baseRev: card.rev, fields })]);
  }

  const parkTx = () => need().transaction(["outbox", "pendingBlobs", "cards"], "readwrite");
  /** Ops paradas (arquivo recusado) do card e o que fazer com elas, numa transação com a fila, os bytes e o card. */
  async function parkedOf(id: string, then: (tx: ReturnType<typeof parkTx>, mine: OutboxEntry[]) => Promise<unknown>[]) {
    const tx = parkTx();
    const mine = (await tx.objectStore("outbox").getAll()).filter((e) => cardOf(e) === id);
    await Promise.all([...then(tx, mine), tx.done]);
  }

  async function remove(id: string) {
    const card = all.value.get(id);
    if (!card || card.deletedAt) return;
    if (card.ingestError === "upload_refused") {
      // O servidor nunca viu este card: apagar é tirar daqui (fila, bytes e card), sem op de apagar que ele recusaria.
      await parkedOf(id, (tx, mine) => [
        ...mine.flatMap((e) => [tx.objectStore("outbox").delete(e.opId), ...(e.blobSha ? [tx.objectStore("pendingBlobs").delete(e.blobSha)] : [])]),
        tx.objectStore("cards").delete(id),
      ]);
      applyCards([[id, null]]);
      return refreshPending();
    }
    notify({ id: newId(), kind: "deleted", cardId: id });
    await commit({ cards: [[id, { ...card, deletedAt: clock.now() }]] }, [mk({ type: "card.delete", cardId: id })]);
  }

  async function restoreMany(ids: string[]) {
    const cs = ids.map((id) => all.value.get(id)).filter((c): c is Card => !!c?.deletedAt);
    if (cs.length) await commit({ cards: cs.map((c) => [c.id, { ...c, deletedAt: null }]) }, cs.map((c) => mk({ type: "card.restore", cardId: c.id })));
  }

  // ---------- etiquetas ----------

  const withTags = (c: Card, tagIds: string[], m = tags.value): Card => ({ ...c, tagIds, isPrivate: tagIds.some((t) => m.get(t)?.normalized === PRIVATE) });
  const tagged = (tagId: string) => [...all.value.values()].filter((c) => c.tagIds.includes(tagId));

  async function attachMany(ids: string[], name: string): Promise<string[]> {
    name = name.trim();
    if (!name) return [];
    const normalized = normalizeTag(name);
    const existing = [...tags.value.values()].find((t) => t.normalized === normalized);
    const tag: Tag = existing ?? { id: newId(), name, normalized, origin: "user", rev: 0 }; // provisória até o pull trazer a do servidor
    const m = new Map(tags.value).set(tag.id, tag);
    const cs = ids.map((id) => all.value.get(id)).filter((c): c is Card => !!c && !c.tagIds.includes(tag.id));
    if (!cs.length) return [];
    await commit(
      { cards: cs.map((c) => [c.id, withTags(c, [...c.tagIds, tag.id], m)]), tags: existing ? [] : [[tag.id, tag]] },
      cs.map((c) => mk({ type: "tag.attach", cardId: c.id, name })),
    );
    return cs.map((c) => c.id);
  }

  async function detachMany(ids: string[], tagId: string) {
    // ponytail: tirar uma etiqueta provisória (rev 0) manda um detach que o servidor recusa; o attach pendente vale.
    const cs = ids.map((id) => all.value.get(id)).filter((c): c is Card => !!c?.tagIds.includes(tagId));
    if (cs.length) await commit({ cards: cs.map((c) => [c.id, withTags(c, c.tagIds.filter((t) => t !== tagId))]) }, cs.map((c) => mk({ type: "tag.detach", cardId: c.id, tagId })));
  }

  /** Etiqueta sai (mesclada ou apagada): os cards trocam por `into` ou só perdem o vínculo. */
  async function dropTag(tagId: string, into: string | null, op: Op) {
    const m = new Map(tags.value);
    m.delete(tagId);
    const cs = tagged(tagId).map((c) => withTags(c, [...new Set(c.tagIds.flatMap((t) => (t !== tagId ? [t] : into ? [into] : [])))], m));
    await commit({ tags: [[tagId, null]], cards: cs.map((c) => [c.id, c]) }, [op]);
  }

  // ---------- coleções ----------

  const nameTaken = (name: string, except?: string) => {
    const n = normalizeTag(name);
    return [...spaceMap.value.values()].some((s) => s.id !== except && s.normalized === n);
  };
  const SPACE_TAKEN = "Já existe uma coleção com esse nome.";

  /** `position`: uma para todos, ou uma por card (null = fim). */
  async function addMany(spaceId: string, ids: string[], position?: number | (number | null)[]): Promise<string[]> {
    const s = spaceMap.value.get(spaceId);
    if (!s) return [];
    const ch = { spaceCards: [] as Row<SpaceCard>[], cards: [] as Row<Card>[] };
    const ops: Op[] = [];
    const added: string[] = [];
    let pos = nextPos(spaceCards.value.get(spaceId) ?? []);
    for (const [i, id] of ids.entries()) {
      const at = Array.isArray(position) ? (position[i] ?? undefined) : position;
      const c = all.value.get(id);
      const k = scKey(spaceId, id);
      const row = scMap.value.get(k);
      if (!c || (row && !row.excluded && (at === undefined || at === row.position))) continue;
      const p = at ?? (row?.excluded ? row.position : null) ?? pos++;
      ch.spaceCards.push([k, { spaceId, cardId: id, excluded: false, position: p, rev: row?.rev ?? 0 }]);
      if (s.kind === "manual" && !c.spaceIds.includes(spaceId)) ch.cards.push([id, { ...c, spaceIds: [...c.spaceIds, spaceId] }]);
      ops.push(mk({ type: "space.add", spaceId, cardId: id, position: p }));
      if (!row || row.excluded) added.push(id);
    }
    if (ops.length) await commit(ch, ops);
    return added;
  }

  async function removeMany(spaceId: string, ids: string[]) {
    const s = spaceMap.value.get(spaceId);
    if (!s) return;
    const ch = { spaceCards: [] as Row<SpaceCard>[], cards: [] as Row<Card>[] };
    const ops: Op[] = [];
    for (const id of ids) {
      const k = scKey(spaceId, id);
      const row = scMap.value.get(k);
      const c = all.value.get(id);
      if (s.kind === "smart") {
        if (row?.excluded) continue;
        ch.spaceCards.push([k, { spaceId, cardId: id, excluded: true, position: row?.position ?? null, rev: row?.rev ?? 0 }]);
      } else {
        if (!row) continue;
        ch.spaceCards.push([k, null]);
        if (c) ch.cards.push([id, { ...c, spaceIds: c.spaceIds.filter((x) => x !== spaceId) }]);
      }
      ops.push(mk({ type: "space.remove", spaceId, cardId: id }));
    }
    if (ops.length) await commit(ch, ops);
  }

  // ---------- exportar / importar ----------

  async function exportZip(): Promise<Blob> {
    const d = need();
    const [cardList, tagList, spaceList, members] = await Promise.all([d.getAll("cards"), d.getAll("tags"), d.getAll("spaces"), d.getAll("spaceCards")]);
    const kept = cardList.filter((c) => !c.deletedAt).sort((a, b) => a.createdAt - b.createdAt);
    const keptIds = new Set(kept.map((c) => c.id));
    const tagName = new Map(tagList.map((t) => [t.id, t.name]));
    const blobIds = new Set(kept.flatMap((c) => [c.primaryBlobId, c.coverBlobId]).filter((b): b is string => !!b));
    let missing = 0;
    const header = ["id", "tipo", "título", "endereço", "texto", "etiquetas", "anotação", "criado em"];
    const files = async function* () {
      yield { name: "cards.jsonl", input: kept.map((c) => JSON.stringify(c)).join("\n") };
      yield { name: "tags.json", input: JSON.stringify(tagList) };
      yield { name: "spaces.json", input: JSON.stringify(spaceList) };
      // Ordem nas coleções e inclusões/exclusões manuais das automáticas (card.spaceIds só lista as manuais).
      yield { name: "spaceCards.json", input: JSON.stringify(members.filter((r) => keptIds.has(r.cardId))) };
      yield {
        name: "cards.csv",
        // BOM: o Excel em pt-BR só lê o arquivo como UTF-8 com ele (sem, "título" vira "tÃ­tulo").
        input: "\uFEFF" + csv([header, ...kept.map((c) => [c.id, c.kind, c.title, c.sourceUrl, c.contentText, c.tagIds.map((t) => tagName.get(t)).join(", "), c.noteText, new Date(c.createdAt).toISOString()].map(cell))]),
      };
      for (const id of blobIds) {
        const res = await deps.fetch(`${deps.origin}/media/${enc(id)}`, { credentials: "include" }).catch(() => null);
        if (res?.ok) yield { name: `arquivos/${id}${extOf(res.headers.get("content-type") ?? "")}`, input: await res.blob() };
        else missing++;
      }
    };
    const zip = await downloadZip(files()).blob();
    if (missing) fail(`${missing} arquivo(s) não puderam ser baixados e ficaram fora da exportação.`);
    return zip;
  }

  type Imported = { sourceId: string; cardId: string; status: OpStatus }[];
  /** POST /v1/imports; soma no relatório e devolve as linhas (id exportado → id do card nesta conta). */
  async function postImport(body: object, report: ImportReport): Promise<Imported> {
    const out = await getJson<{ cards: Imported }>("/v1/imports", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    for (const r of out.cards) r.status === "applied" ? report.created++ : r.status === "duplicate" ? report.duplicates++ : report.failed++;
    return out.cards;
  }

  /**
   * .md/.txt também pelo POST /v1/imports, 100 por pedido: o servidor liga os [[ ]] entre as notas importadas em
   * qualquer ordem (a criação pela fila só liga a notas que já existem). Id a partir do nome e do conteúdo: importar o
   * mesmo arquivo de novo não duplica.
   */
  async function importNotes(fs: File[], report: ImportReport) {
    for (let i = 0; i < fs.length; i += 100) {
      const chunk = fs.slice(i, i + 100);
      try {
        const cards = await Promise.all(
          chunk.map(async (f) => {
            const n = await noteOf(f);
            const id = `md-${(await sha256Hex(new Blob([f.name, "\n", n.text]))).slice(0, 32)}`;
            return { id, kind: "note", createdAt: clock.now(), title: n.title, content: n.doc, contentText: n.text };
          }),
        );
        await postImport({ cards }, report);
      } catch (e) {
        report.failed += chunk.length;
        if ((e as Error).cause !== NOTIFIED) fail(`Não deu para importar ${chunk.length} arquivo(s) de texto.`);
      }
    }
  }

  /** Zip exportado → blobs (PUT) → POST /v1/imports em lotes (≤ 100 ops: coleções + cards + pertenças). */
  async function importZip(f: File, report: ImportReport) {
    const entries = await unzip(f).catch(() => null);
    const jsonl = entries?.get("cards.jsonl");
    if (!entries || !jsonl) {
      fail("Este arquivo não é uma exportação do fai.g.");
      throw notified("invalid");
    }
    const read = async <T>(name: string): Promise<T[]> => {
      const v: unknown = JSON.parse((await entries.get(name)?.text()) ?? "[]");
      return Array.isArray(v) ? v : [];
    };
    const cardList = (await jsonl.text()).split("\n").filter(Boolean).map((l) => JSON.parse(l) as Card);
    const [tagList, spaceList, members] = [await read<Tag>("tags.json"), await read<Space>("spaces.json"), await read<SpaceCard>("spaceCards.json")];
    const blobs: { id: string; sha256: string }[] = [];
    for (const [name, data] of entries) {
      const id = name.match(/^arquivos\/([^/.]+)/)?.[1];
      if (!id) continue;
      // Arquivo recusado (formato): o card dele volta recusado no lote, e o aviso já saiu.
      const up = await putBlob(data).catch((e: Error) => (e.message === "offline" ? Promise.reject(e) : null));
      if (up) blobs.push({ id, sha256: up.sha256 });
    }
    const here = new Map<string, string>(); // id exportado → id do card nesta conta
    const post = async (body: object) => {
      for (const r of await postImport(body, report)) if (r.status !== "rejected") here.set(r.sourceId, r.cardId);
    };
    // Coleções antes: as automáticas e as vazias não aparecem em nenhum card.
    for (let i = 0; i < spaceList.length; i += 100) await post({ cards: [], spaces: spaceList.slice(i, i + 100) });
    let chunk: Card[] = [];
    let cost = 0;
    const flush = async () => {
      if (!chunk.length) return;
      const sids = new Set(chunk.flatMap((c) => c.spaceIds ?? []));
      const tids = new Set(chunk.flatMap((c) => c.tagIds ?? []));
      const bids = new Set(chunk.flatMap((c) => [c.primaryBlobId, c.coverBlobId]));
      await post({
        cards: chunk,
        tags: tagList.filter((t) => tids.has(t.id)).map(({ id, name }) => ({ id, name })),
        spaces: spaceList.filter((s) => sids.has(s.id)),
        blobs: blobs.filter((b) => bids.has(b.id)),
      });
      chunk = [];
      cost = 0;
    };
    for (const c of cardList) {
      const n = 1 + 2 * (c.spaceIds?.length ?? 0); // card + (coleção + pertença) por coleção
      if (cost + n > 100) await flush();
      chunk.push(c);
      cost += n;
    }
    await flush();

    // O /v1/imports põe os cards das coleções manuais no fim, sem posição, e não conhece as pertenças das automáticas:
    // a ordem arrastada e as inclusões/exclusões manuais vão pela fila, depois que o pull trouxe coleções e cards.
    if (!members.length) return;
    await sync();
    const byName = new Map([...spaceMap.value.values()].map((s) => [s.normalized, s]));
    for (const sp of spaceList) {
      const s = byName.get(normalizeTag(String(sp.name)));
      const rows = members.filter((r) => r?.spaceId === sp.id && here.has(r.cardId)).sort(byPos);
      if (!s || !rows.length) continue;
      const kept = rows.filter((r) => r.excluded !== true);
      await addMany(s.id, kept.map((r) => here.get(r.cardId)!), kept.map((r) => (Number.isFinite(r.position) ? r.position : null)));
      if (s.kind === "smart") await removeMany(s.id, rows.filter((r) => r.excluded === true).map((r) => here.get(r.cardId)!));
    }
  }

  // ---------- sessão ----------

  async function start(accountId: string) {
    if (!stopped) return;
    account = accountId;
    const d = (db = await openLocal(accountId, () => {
      if (db !== d) return;
      // Outra aba saiu da conta (vai apagar o banco) ou abriu um esquema mais novo: esta larga a réplica em memória
      // (§7.10) e a UI recarrega, pegando a sessão e o app atuais.
      void stop();
      batch(() => {
        all.value = new Map();
        for (const s of Object.values(maps)) (s as Signal<Map<string, unknown>>).value = new Map();
        prefsRow.value = null;
        notices.value = [];
        setStatus({ syncing: false, pending: 0, error: "signed_out" });
      });
    }));
    const [cardList, tagList, spaceList, scList, pinList, linkList, outbox, cp, last, pr] = await Promise.all([
      d.getAll("cards"), d.getAll("tags"), d.getAll("spaces"), d.getAll("spaceCards"), d.getAll("pins"), d.getAll("links"),
      d.getAll("outbox"), d.get("meta", "checkpoint"), d.get("meta", "lastSyncAt"), d.get("meta", "prefs"),
    ]);
    const urls = new Map<string, string>();
    for (const e of outbox) {
      if (e.op.type !== "card.create" || !e.blobSha) continue;
      const pending = await d.get("pendingBlobs", e.blobSha);
      // Imagem, PDF, vídeo e áudio ainda na fila: a cópia local segue visível e tocável depois de recarregar.
      if (pending) urls.set(e.op.cardId, URL.createObjectURL(pending.file));
    }
    checkpoint = (cp?.value as number | undefined) ?? 0;
    batch(() => {
      all.value = new Map(cardList.map((c) => [c.id, c]));
      tags.value = new Map(tagList.map((t) => [t.id, t]));
      spaceMap.value = new Map(spaceList.map((s) => [s.id, s]));
      scMap.value = new Map(scList.map((r) => [scKey(r.spaceId, r.cardId), r]));
      pinMap.value = new Map(pinList.map((p) => [p.cardId, p]));
      linkMap.value = new Map(linkList.map((l) => [l.id, l]));
      prefsRow.value = (pr?.value as Prefs | undefined) ?? null;
      previews.value = urls;
      notices.value = [];
      status.value = { online: true, syncing: false, pending: sendable(outbox).length, lastSyncAt: (last?.value as number | undefined) ?? null, error: null };
    });
    stopped = false;
    void deps.persist().catch(() => {}); // primeiro uso depois de entrar, não só depois de um compartilhamento (D32)
    deps.events?.addEventListener("focus", onWake);
    deps.events?.addEventListener("online", onWake);
    deps.events?.addEventListener("offline", onOffline);
    connect();
    void sync();
  }

  async function stop() {
    stopped = true;
    gen++;
    running = null; // o ciclo da sessão que acabou termina sozinho (need lança); um start novo não espera por ele
    inflight.clear();
    own.clear();
    clock.clearTimeout(timer);
    clock.clearTimeout(wsTimer);
    ws?.close();
    ws = null;
    deps.events?.removeEventListener("focus", onWake);
    deps.events?.removeEventListener("online", onWake);
    deps.events?.removeEventListener("offline", onOffline);
    for (const url of previews.value.values()) URL.revokeObjectURL(url);
    previews.value = new Map();
    db?.close();
    db = null;
  }

  return {
    cards,
    trash,
    tags,
    status,
    notices,
    previews,
    progress,
    spaces,
    spaceCards,
    pins: computed(() => pinned.value.slice(0, LIMITS.pins)),
    links: linkMap,
    prefs,
    sync,
    checkpoint: () => checkpoint,
    start,
    stop,
    get: (id) => all.value.get(id),
    outgoing: (id) => linkIndex.value.from.get(id) ?? [],
    backlinks: (id) => linkIndex.value.to.get(id) ?? [],

    spaceCardIds(spaceId, matched = []) {
      const rows = spaceCards.value.get(spaceId) ?? [];
      const excluded = new Set(rows.filter((r) => r.excluded).map((r) => r.cardId));
      const kept = rows.filter((r) => !r.excluded).map((r) => r.cardId);
      const out = spaceMap.value.get(spaceId)?.kind === "smart" ? [...matched.filter((id) => !excluded.has(id)), ...kept] : kept;
      return [...new Set(out)].filter((id) => live(all.value.get(id)));
    },

    createNote: (doc, text) => note(doc, text),
    createLink,
    createImage: createFile,
    createFile,
    update,
    remove,
    restore: (id) => restoreMany([id]),
    attachTag: async (id, name) => void (await attachMany([id], name)),
    detachTag: (id, tagId) => detachMany([id], tagId),
    dismissNotice: (nid) => (notices.value = notices.value.filter((n) => n.id !== nid)),

    async retry(id) {
      const card = all.value.get(id);
      if (card?.ingestError === "upload_refused") {
        // Arquivo recusado: a op volta para a fila e o mesmo card tenta subir de novo.
        const up: Card = { ...card, ingestState: "uploading", ingestError: null };
        await parkedOf(id, (tx, mine) => [...mine.filter(parked).map((e) => tx.objectStore("outbox").put({ ...e, lastError: null })), tx.objectStore("cards").put(up)]);
        applyCards([[id, up]]);
        await refreshPending();
        void sync();
        return id;
      }
      if (card?.kind !== "link" || !card.sourceUrl) return null;
      const names = card.tagIds.map((t) => tags.value.get(t)?.name).filter((n): n is string => !!n);
      const keep = { ...(card.note && { note: card.note, noteText: card.noteText }), ...(card.titleOrigin === "user" && { title: card.title }) };
      return createLink(card.sourceUrl, names, keep, card);
    },

    async review(id) {
      const c = all.value.get(id);
      if (c) await commit({ cards: [[id, { ...c, lastReviewedAt: clock.now() }]] }, [mk({ type: "card.review", cardId: id })]);
    },
    setAction: (id, action) => update(id, { action }),
    setCompleted: (id, done) => update(id, { completedAt: done ? clock.now() : null }),
    setCover: (id, blobId) => update(id, { coverBlobId: blobId }),
    async setPrivate(id, on) {
      const c = all.value.get(id);
      if (on) return void (await attachMany([id], PRIVATE));
      const t = c?.tagIds.find((t) => tags.value.get(t)?.normalized === PRIVATE);
      if (t) await detachMany([id], t);
    },

    async renameTag(tagId, name) {
      const tag = tags.value.get(tagId);
      name = name.trim();
      if (!tag || !name || name === tag.name) return;
      const normalized = normalizeTag(name);
      if ([...tags.value.values()].some((t) => t.id !== tagId && t.normalized === normalized)) {
        return void fail("Já existe uma etiqueta com esse nome. Para juntar as duas, use mesclar.");
      }
      const next = { ...tag, name, normalized };
      const m = new Map(tags.value).set(tagId, next);
      // Renomear para "privado" (ou tirar esse nome) muda quem é privado.
      const cs = tagged(tagId).map((c) => withTags(c, c.tagIds, m)).filter((c) => c.isPrivate !== all.value.get(c.id)?.isPrivate);
      await commit({ tags: [[tagId, next]], cards: cs.map((c) => [c.id, c]) }, [mk({ type: "tag.rename", tagId, name })]);
    },
    async mergeTag(tagId, into) {
      if (tagId !== into && tags.value.has(tagId) && tags.value.has(into)) await dropTag(tagId, into, mk({ type: "tag.merge", tagId, into }));
    },
    async deleteTag(tagId) {
      if (tags.value.has(tagId)) await dropTag(tagId, null, mk({ type: "tag.delete", tagId }));
    },

    async createSpace({ name, color, kind = "manual", query }) {
      name = name.trim();
      const q = kind === "smart" ? query?.trim() || null : null;
      if (!name || !HEX.test(color) || (kind === "smart") !== !!q) throw new Error("invalid");
      if (nameTaken(name)) {
        fail(SPACE_TAKEN);
        return null;
      }
      const id = newId();
      const position = nextPos(spaceMap.value.values());
      const space: Space = { id, name, normalized: normalizeTag(name), color, kind, query: q, queryVersion: q ? 1 : null, position, rev: 0 };
      await commit({ spaces: [[id, space]] }, [mk({ type: "space.create", spaceId: id, data: { name, color, kind, position, ...(q ? { query: q } : {}) } })]);
      return id;
    },
    async updateSpace(id, fields) {
      const s = spaceMap.value.get(id);
      if (!s) return;
      const f = { ...fields, ...(fields.name !== undefined && { name: fields.name.trim() }) };
      if (f.name === "" || (f.color !== undefined && !HEX.test(f.color)) || (f.query !== undefined && (s.kind !== "smart" || !f.query.trim()))) throw new Error("invalid");
      if (f.name !== undefined && nameTaken(f.name, id)) return void fail(SPACE_TAKEN);
      const next: Space = { ...s, ...f, ...(f.name !== undefined && { normalized: normalizeTag(f.name) }) };
      await commit({ spaces: [[id, next]] }, [mk({ type: "space.update", spaceId: id, fields: f })]);
    },
    async deleteSpace(id) {
      if (!spaceMap.value.has(id)) return;
      const members = [...scMap.value.values()].filter((r) => r.spaceId === id).map((r): Row<SpaceCard> => [scKey(id, r.cardId), null]);
      const cs = [...all.value.values()].filter((c) => c.spaceIds.includes(id)).map((c): Row<Card> => [c.id, { ...c, spaceIds: c.spaceIds.filter((x) => x !== id) }]);
      await commit({ spaces: [[id, null]], spaceCards: members, cards: cs }, [mk({ type: "space.delete", spaceId: id })]);
    },
    addToSpace: async (spaceId, cardId, position) => void (await addMany(spaceId, [cardId], position)),
    removeFromSpace: (spaceId, cardId) => removeMany(spaceId, [cardId]),
    async reorderSpaceCard(spaceId, cardId, position) {
      const row = scMap.value.get(scKey(spaceId, cardId));
      if (row && !row.excluded && row.position !== position) await addMany(spaceId, [cardId], position);
    },

    async pin(cardId, position) {
      const row = pinMap.value.get(cardId);
      if (!live(all.value.get(cardId))) return false;
      if (row && position === undefined) return true;
      const others = pinned.value.filter((p) => p.cardId !== cardId);
      if (others.length >= LIMITS.pins) {
        fail(PIN_FULL);
        return false;
      }
      const p = position ?? nextPos(others);
      await commit({ pins: [[cardId, { cardId, position: p, rev: row?.rev ?? 0 }]] }, [mk({ type: "pin.set", cardId, position: p })]);
      return true;
    },
    async unpin(cardId) {
      if (pinMap.value.has(cardId)) await commit({ pins: [[cardId, null]] }, [mk({ type: "pin.unset", cardId })]);
    },
    async reorderPins(cardIds) {
      const moved = cardIds.flatMap((id, i): Pin[] => {
        const row = pinMap.value.get(id);
        return row && row.position !== i ? [{ ...row, position: i }] : [];
      });
      if (moved.length) await commit({ pins: moved.map((p) => [p.cardId, p]) }, moved.map((p) => mk({ type: "pin.set", cardId: p.cardId, position: p.position })));
    },
    async setPrefs(values) {
      const v = Object.fromEntries(Object.entries(values).filter(prefOk)) as Prefs["values"];
      if (!Object.keys(v).length) return;
      const cur = prefsRow.value;
      await commit({ prefs: { values: { ...cur?.values, ...v }, rev: cur?.rev ?? 0 } }, [mk({ type: "prefs.update", values: v })]);
    },

    async applyMany(ids, action: BatchAction) {
      if (action.kind === "delete") {
        const now = clock.now();
        const cs = ids.map((id) => all.value.get(id)).filter(live);
        if (cs.length) await commit({ cards: cs.map((c) => [c.id, { ...c, deletedAt: now }]) }, cs.map((c) => mk({ type: "card.delete", cardId: c.id })));
        return () => restoreMany(cs.map((c) => c.id));
      }
      if (action.kind === "tag") {
        const done = await attachMany(ids, action.name);
        const normalized = normalizeTag(action.name);
        // A etiqueta provisória pode ter virado a do servidor: procura pelo nome na hora de desfazer.
        return async () => {
          const t = [...tags.value.values()].find((x) => x.normalized === normalized);
          if (t) await detachMany(done, t.id);
        };
      }
      // Numa automática, desfazer teria de apagar as inclusões novas, e o servidor só sabe trocá-las por exclusões
      // (esconderiam o card da consulta para sempre): o lote vale só para coleções manuais (aceite 7).
      if (spaceMap.value.get(action.spaceId)?.kind === "smart") {
        fail("Coleção automática se enche pela busca. Para incluir um card nela, use o menu do próprio card.");
        return async () => {};
      }
      const added = await addMany(action.spaceId, ids);
      return () => removeMany(action.spaceId, added);
    },

    reader: async (id) => (await rest(`/v1/cards/${enc(id)}/reader`)).text(),
    similar: async (id, k = 24) => (await getJson<{ ids: string[] }>(`/v1/cards/${enc(id)}/similar?k=${k}`)).ids,
    async reprocess(id) {
      await rest(`/v1/cards/${enc(id)}/reprocess`, { method: "POST" });
      void sync();
    },
    share: (id) => getJson(`/v1/cards/${enc(id)}/share`, { method: "POST" }),
    revokeShare: async (tokenId) => void (await rest(`/v1/share/${enc(tokenId)}`, { method: "DELETE" })),
    usage: () => getJson("/v1/me/usage"),
    devices: async () => (await getJson<{ devices: Device[] }>("/v1/devices")).devices,
    pairDevice: () => getJson("/v1/devices/pair", { method: "POST" }),
    revokeDevice: async (id) => void (await rest(`/v1/devices/${enc(id)}`, { method: "DELETE" })),
    serverSearch: (q, limit = 50) => getJson(`/v1/search?q=${enc(q)}&limit=${limit}`),
    uploadBlob: async (file) => (await putBlob(file)).blobId,

    exportZip,
    async downloadExport() {
      const url = URL.createObjectURL(await exportZip());
      const a = document.createElement("a");
      a.href = url;
      a.download = `faig-${new Date(clock.now()).toISOString().slice(0, 10)}.zip`;
      a.click();
      clock.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },

    async importFiles(files) {
      need();
      const report: ImportReport = { created: 0, duplicates: 0, failed: 0 };
      const texts: File[] = [];
      for (const f of files) {
        if (/\.(md|markdown|txt)$/i.test(f.name)) {
          texts.push(f);
          continue;
        }
        try {
          if (!/\.zip$/i.test(f.name)) throw new Error("unsupported_type");
          await importZip(f, report);
        } catch (e) {
          report.failed++;
          if ((e as Error).cause !== NOTIFIED) fail(`Não deu para importar ${f.name}.`);
        }
      }
      await importNotes(texts, report);
      await sync();
      return report;
    },

    async rebuildLocalCopy() {
      const acc = account;
      need();
      await sync();
      const s = status.value;
      if (!acc || s.pending || !s.online || s.error) {
        // Nada é apagado: o que está na fila só existe aqui.
        fail("A cópia local não foi refeita: há alterações não enviadas ou falta conexão. Tente de novo com rede.");
        return null;
      }
      await stop();
      await clearLocalData(acc);
      await start(acc);
      await sync();
      return { cards: cards.value.length, trash: trash.value.length, tags: tags.value.size, spaces: spaceMap.value.size };
    },

    async consumeSharedItems() {
      need();
      const inbox = await openShareInbox();
      const ids: string[] = [];
      const save = (job: () => Promise<string>) =>
        job().then(
          (id) => void ids.push(id),
          (e: Error) => void (e.cause !== NOTIFIED && fail("Um item compartilhado não pôde ser salvo.")),
        );
      try {
        for (const item of await inbox.getAll("shareInbox")) {
          // `url` vale como veio; muitos apps mandam o link dentro de `text` (§10.4).
          const given = item.url?.trim();
          const url = (given && /^https?:\/\/\S+$/i.test(given) ? given : undefined) ?? urlIn(item.url) ?? urlIn(item.text);
          // Trecho selecionado: destaque com a origem. Comentário ("olha isso"): card de link com ele na anotação.
          const shared = url ? sharedExcerpt(item.text, url, item.title) : null;
          if (url && shared?.selection) await save(() => excerpt(shared.text, url, item.title));
          else if (url) await save(() => commentedLink(url, shared?.text));
          else if (item.text?.trim()) await save(() => note(textDoc(item.text!), item.text!));
          for (const f of item.files ?? []) await save(() => (f.type.startsWith("text/") ? textFile(f) : createFile(f)));
          await inbox.delete("shareInbox", item.id);
        }
      } finally {
        inbox.close();
      }
      return ids;
    },
  };
}
